import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { apiPublic } from "@/lib/api";
import { ArrowLeft, Linkedin, Mail, User as UserIcon } from "lucide-react";

export default function GovernanceProfile() {
    const { slug } = useParams();
    const [member, setMember] = useState(null);
    const [others, setOthers] = useState([]);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        apiPublic.get(`/governance/${slug}`).then(r => setMember(r.data)).catch(() => setNotFound(true));
        apiPublic.get("/governance").then(r => setOthers(r.data.filter(m => m.slug !== slug))).catch(() => {});
    }, [slug]);

    if (notFound) {
        return (
            <div className="min-h-screen bg-[#0a0c1c] text-white flex flex-col items-center justify-center p-6">
                <h1 className="font-display text-5xl">Profilo non trovato</h1>
                <Link to="/" className="btn-ghost mt-8" data-testid="back-home">Torna alla home</Link>
            </div>
        );
    }
    if (!member) return <div className="min-h-screen bg-[#0a0c1c] text-zinc-500 flex items-center justify-center">Caricamento…</div>;

    return (
        <div className="min-h-screen bg-[#0a0c1c] text-white" data-testid="governance-profile">
            <nav className="border-b border-white/5 bg-[#0a0c1c]/80 backdrop-blur sticky top-0 z-50">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3" data-testid="gov-nav-logo">
                        <div className="h-6 w-6 rounded-full bg-tropical grain" />
                        <span className="font-display text-lg">StartupItalia<span className="text-gradient-tropical">.</span></span>
                    </Link>
                    <Link to="/governance" className="btn-ghost" data-testid="gov-back-governance">
                        <ArrowLeft className="h-4 w-4" /> Tutta la governance
                    </Link>
                </div>
            </nav>

            <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-20 pb-24">
                <div className="grid grid-cols-12 gap-10">
                    {/* Photo */}
                    <div className="col-span-12 lg:col-span-5">
                        <div className="bento p-0 overflow-hidden aspect-[4/5] relative grain" data-testid="gov-photo">
                            {member.photo_url ? (
                                <img src={member.photo_url} alt={member.nome} className="w-full h-full object-cover" />
                            ) : (
                                <div className="w-full h-full bg-tropical-soft flex items-center justify-center">
                                    <UserIcon className="h-32 w-32 text-white/50" />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Info */}
                    <div className="col-span-12 lg:col-span-7">
                        <span className="label-tag" data-testid="gov-role-label">— Governance</span>
                        <h1 className="font-display text-6xl lg:text-7xl mt-4 tracking-tighter" data-testid="gov-name">{member.nome}</h1>
                        <p className="font-serif-italic text-3xl lg:text-4xl text-gradient-tropical mt-3" data-testid="gov-title">{member.titolo}</p>

                        {member.bio && (
                            <div className="prose prose-invert mt-10 max-w-2xl text-zinc-300 leading-relaxed" data-testid="gov-bio">
                                {member.bio.split("\n\n").map((para, i) => <p key={`bio-p-${i}-${para.slice(0, 20)}`} className="mb-4">{para}</p>)}
                            </div>
                        )}

                        <div className="mt-10 flex flex-wrap gap-3" data-testid="gov-contacts">
                            {member.linkedin && (
                                <a href={member.linkedin} target="_blank" rel="noreferrer" className="btn-ghost" data-testid="gov-linkedin">
                                    <Linkedin className="h-4 w-4" /> LinkedIn
                                </a>
                            )}
                            {member.email && (
                                <a href={`mailto:${member.email}`} className="btn-ghost" data-testid="gov-email">
                                    <Mail className="h-4 w-4" /> {member.email}
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Other members */}
            {others.length > 0 && (
                <section className="border-t border-white/5 py-20">
                    <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
                        <span className="label-tag">— Altri membri</span>
                        <h2 className="font-display text-4xl lg:text-5xl mt-3 tracking-tighter">Le altre persone <span className="font-serif-italic text-gradient-tropical">in board</span>.</h2>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
                            {others.map(o => (
                                <Link key={o.id} to={`/governance/${o.slug}`} className="bento p-0 overflow-hidden group" data-testid={`other-${o.slug}`}>
                                    <div className="aspect-[4/5] bg-tropical-soft relative grain">
                                        {o.photo_url && <img src={o.photo_url} alt={o.nome} className="w-full h-full object-cover" />}
                                    </div>
                                    <div className="p-5">
                                        <div className="font-display text-lg">{o.nome}</div>
                                        <div className="text-zinc-500 text-xs uppercase tracking-widest font-grotesk mt-1">{o.titolo}</div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
}
